import { ChangeDetectionStrategy, Component, ElementRef, computed, input, model, signal, viewChild } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { TranslatePipe } from '@ngx-translate/core';

/**
 * Multi-value chip/tag input component.
 *
 * Allows users to enter multiple values as chips (tags).
 * Supports Enter/comma/tab to add, backspace to remove last,
 * paste to add multiple (split by comma/newline/tab),
 * per-chip validation, duplicate detection, and max item limit.
 *
 * @example
 * ```html
 * <zag-chip-input
 *   [(values)]="emailList"
 *   placeholder="Type email and press Enter"
 *   [validator]="isValidEmail"
 *   [maxItems]="100"
 * />
 * ```
 */
@Component({
  selector: 'zag-chip-input',
  imports: [FormsModule, TranslatePipe],
  changeDetection: ChangeDetectionStrategy.OnPush,
  template: `
    <div
      class="zag-chip-input"
      [class.zag-chip-input--focused]="isFocused()"
      (click)="focusInput()"
      (keydown.enter)="focusInput()"
      role="group"
      [attr.aria-label]="placeholder()"
    >
      @for (chip of values(); track chip; let i = $index) {
        <div class="zag-chip-input__chip" role="listitem">
          <span class="zag-chip-input__chip-label">{{ chip }}</span>
          <button
            type="button"
            class="zag-chip-input__chip-remove"
            (click)="removeChip(i, $event)"
            [attr.aria-label]="('common.remove' | translate) + ' ' + chip"
          >
            &times;
          </button>
        </div>
      }

      @if (values().length < maxItems()) {
        <input
          #inputEl
          type="text"
          class="zag-chip-input__input"
          [(ngModel)]="inputValue"
          [placeholder]="values().length === 0 ? placeholder() : ''"
          (keydown)="onKeyDown($event)"
          (blur)="onBlur()"
          (focus)="onFocus()"
          (paste)="onPaste($event)"
          [attr.aria-label]="placeholder()"
          autocomplete="off"
        />
      }
    </div>
  `,
  styles: `
    .zag-chip-input {
      display: flex;
      flex-wrap: wrap;
      gap: var(--spacing-xs);
      padding: var(--spacing-xs) var(--spacing-sm);
      border: 1px solid var(--input-border);
      border-radius: var(--border-radius-sm);
      background-color: var(--input-background);
      cursor: text;
      min-height: 38px;
      align-items: center;
      transition:
        border-color var(--transition-duration-fast),
        box-shadow var(--transition-duration-fast);
    }

    .zag-chip-input--focused {
      border-color: var(--input-border-focus);
      box-shadow: 0 0 0 2px color-mix(in srgb, var(--color-primary) 20%, transparent);
    }

    .zag-chip-input__chip {
      display: inline-flex;
      align-items: center;
      gap: var(--spacing-xs);
      background-color: var(--surface-color-primary);
      color: var(--text-color-primary);
      padding: 2px var(--spacing-sm);
      border-radius: var(--border-radius-lg);
      font-size: var(--font-size-caption);
      max-width: 200px;
    }

    .zag-chip-input__chip-label {
      overflow: hidden;
      text-overflow: ellipsis;
      white-space: nowrap;
    }

    .zag-chip-input__chip-remove {
      display: flex;
      align-items: center;
      background: none;
      border: none;
      cursor: pointer;
      padding: 0;
      color: var(--text-color-secondary);
      font-size: 16px;
      line-height: 1;
      opacity: 0.7;

      &:hover {
        opacity: 1;
      }
    }

    .zag-chip-input__input {
      flex: 1;
      min-width: 140px;
      border: none;
      outline: none;
      background: transparent;
      font-size: var(--font-size-body);
      color: var(--input-text);
      padding: 0;

      &::placeholder {
        color: var(--input-placeholder);
      }
    }
  `,
})
export class ChipInputComponent {
  readonly values = model.required<string[]>();
  readonly placeholder = input<string>('');
  readonly validator = input<(value: string) => boolean>(() => true);
  readonly maxItems = input<number>(100);
  readonly duplicateCheck = input<boolean>(true);

  protected readonly isFocused = signal(false);
  protected readonly inputValue = signal('');

  private readonly inputEl = viewChild<ElementRef<HTMLInputElement>>('inputEl');

  protected readonly canAddMore = computed(() => this.values().length < this.maxItems());

  focusInput(): void {
    this.inputEl()?.nativeElement.focus();
  }

  protected onFocus(): void {
    this.isFocused.set(true);
  }

  protected onBlur(): void {
    this.isFocused.set(false);
    const val = this.inputValue();
    if (val.trim()) {
      this.tryAddChip(val.trim());
      this.inputValue.set('');
    }
  }

  protected onKeyDown(event: KeyboardEvent): void {
    const val = this.inputValue();

    if (event.key === 'Enter' || event.key === ',') {
      event.preventDefault();
      if (val.trim()) {
        this.tryAddChip(val.trim().replace(/,$/, '').trim());
        this.inputValue.set('');
      }
      return;
    }

    if (event.key === 'Tab' && val.trim()) {
      event.preventDefault();
      this.tryAddChip(val.trim());
      this.inputValue.set('');
      return;
    }

    if (event.key === 'Backspace' && !val) {
      const current = this.values();
      if (current.length > 0) {
        this.values.set(current.slice(0, -1));
      }
    }
  }

  protected onPaste(event: ClipboardEvent): void {
    event.preventDefault();
    const text = event.clipboardData?.getData('text') ?? '';
    const items = text
      .split(/[,\n;]+/)
      .map(s => s.trim())
      .filter(s => s.length > 0);

    for (const item of items) {
      if (this.values().length >= this.maxItems()) break;
      this.tryAddChip(item);
    }
    this.inputValue.set('');
  }

  protected removeChip(index: number, event: MouseEvent): void {
    event.stopPropagation();
    const current = this.values();
    this.values.set([...current.slice(0, index), ...current.slice(index + 1)]);
  }

  private tryAddChip(value: string): void {
    if (!value) return;
    if (this.values().length >= this.maxItems()) return;
    if (this.duplicateCheck() && this.values().includes(value)) return;
    if (!this.validator()(value)) return;
    this.values.set([...this.values(), value]);
  }
}
